import * as React from "react";

import {
  Paper,
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableSortLabel,
  TableCell,
  Skeleton,
} from "mpulse-design-system";
import { Box, styled } from "@mui/material";

import { pluralize } from "../../utils/utils";
import constants from "../../constants";

import AudioReviewActionPopup from "../AudioReviewActionPopup/AudioReviewActionPopup";
import AudioReviewMenu from "../AudioReviewMenu/AudioReviewMenu";
import AudioPlayer from "../AudioPlayer/AudioPlayer";

import { orderBy } from "lodash";

const DataTable = ({
  type,
  value,
  index,
  columnsMap,
  data,
  metadataLabel = "",
  accountId,
  loading = false,
}) => {
  const [popupProps, setPopupProps] = React.useState({ hidden: true }),
    columns = Object.keys(columnsMap),
    dataCount = data.length,
    isAudioView = type === "AUDIO";

  const [columnToSort, setColumnToSort] = React.useState("");
  const [sortDirection, setSortDirection] = React.useState("asc");
  const [orderedData, setOrderedData] = React.useState(data);

  const inverseDirection = {
    asc: "desc",
    desc: "asc",
  };

  const sortingColumns = ["date", "client_department_id", "metadata_name"];

  const handleSort = (columnName) => {
    setColumnToSort(columnName);
    setSortDirection(
      columnToSort === columnName ? inverseDirection[sortDirection] : "asc"
    );
    setOrderedData(orderBy(data, columnName, sortDirection));
  };

  React.useEffect(() => {
    setOrderedData(data);
  }, [data]);

  return (
    <Box hidden={value !== index}>
      <Paper
        variant="elevation"
        elevation={0}
        square
        sx={{
          padding: (theme) => `${theme.spacing(2)} ${theme.spacing(3)}`,
          backgroundColor: "white",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Typography color="textTertiary">
              Showing {pluralize("item", { count: dataCount })}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      <TableContainer
        sx={{ borderRadius: "unset !important", boxSizing: "border-box" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column, i) => {
                return (
                  <StyledTableCell
                    key={i}
                    sx={{ width: columnsMap[column].size }}
                  >
                    {columnsMap[column].title}
                    {
                      // sorting label
                      sortingColumns.includes(column) ? ( // only sorting columns get labels
                        <TableSortLabel
                          active={column === columnToSort ? true : false} // only sorted column label is active
                          direction={
                            column === columnToSort ? sortDirection : "asc"
                          } // label direction
                          onClick={() => handleSort(column)} // sorting actions
                        />
                      ) : null
                    }
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <StyledTableCell colSpan={columns.length + 1}>
                  <Skeleton />
                </StyledTableCell>
              </TableRow>
            ) : dataCount === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={columns.length}>
                  No Audio to Review.
                </StyledTableCell>
              </TableRow>
            ) : (
              orderedData.map((item, i) => {
                return (
                  <TableRow key={i}>
                    {columns.map((column, x) => {
                      return (
                        <StyledTableCell
                          key={`${i}-${x}`}
                          sx={{ overflow: "initial" }}
                        >
                          {isAudioView && column === "status" ? (
                            <AudioReviewMenu
                              {...item}
                              index={i}
                              status={constants.metadata.statuses[item[column]]}
                              setPopupProps={setPopupProps}
                            />
                          ) : isAudioView && column === "audioPlayer" ? (
                            <AudioPlayer
                              url={item["filepath"]}
                              duration={item["duration"]}
                            />
                          ) : (
                            item[column]
                          )}
                        </StyledTableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isAudioView ? (
        <AudioReviewActionPopup
          {...popupProps}
          setPopupProps={setPopupProps}
          metadataLabel={metadataLabel}
          accountId={accountId}
        />
      ) : null}
    </Box>
  );
};

const StyledTableCell = styled(TableCell)(() => ({
  "&.MuiTableCell-root": {
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
}));

export default DataTable;
